import {ConsoleLogger} from 'aws-amplify/utils';

const logger = new ConsoleLogger('lazyWithRetry');
const RELOAD_STORAGE_KEY = 'pp:lazy-retry-reloaded';
const LAST_RELOAD_AT_STORAGE_KEY = 'pp:lazy-retry-last-reload-at';
const RELOAD_COOLDOWN_MS = 30_000;

// In-memory fallback for environments where sessionStorage is entirely
// unavailable (e.g. Safari private browsing). These survive the current page
// lifecycle long enough to prevent a reload loop before navigation completes.
let memoryHasReloaded = false;
let memoryLastReloadAt: number | null = null;

// Wraps sessionStorage to silently swallow errors in Safari private browsing,
// where sessionStorage throws SecurityError / QuotaExceededError on every access.
const safeSessionStorage = {
  getItem: (key: string): string | null => {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Ignore — retry flag is best-effort; a missing flag just means we may
      // attempt one extra reload in private browsing, which is acceptable.
    }
  },
  removeItem: (key: string): void => {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Ignore
    }
  },
};

// Accepts `unknown` so callers never need to cast and non-Error throws are handled safely.
// Extracts name/message from any thrown shape: Error subclasses, DOMException, plain
// objects, and strings — some browsers surface dynamic-import failures as non-Error values.
const isChunkLoadError = (error: unknown): boolean => {
  let name = '';
  let message = '';

  if (error instanceof Error) {
    name = error.name;
    message = error.message.toLowerCase();
  } else if (error !== null && typeof error === 'object') {
    const obj = error as Record<string, unknown>;
    name = String(obj['name'] ?? '');
    message = String(obj['message'] ?? '').toLowerCase();
  } else if (typeof error === 'string') {
    message = error.toLowerCase();
  }

  return (
    name === 'ChunkLoadError' ||
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('loading chunk')
  );
};

const hasRecentReloadAttempt = () => {
  // Check in-memory fallback first — guards when sessionStorage is unavailable.
  if (memoryLastReloadAt !== null && Date.now() - memoryLastReloadAt < RELOAD_COOLDOWN_MS) {
    return true;
  }

  const rawLastReloadAt = safeSessionStorage.getItem(LAST_RELOAD_AT_STORAGE_KEY);

  if (!rawLastReloadAt) {
    return false;
  }

  const lastReloadAt = Number(rawLastReloadAt);

  if (Number.isNaN(lastReloadAt)) {
    safeSessionStorage.removeItem(LAST_RELOAD_AT_STORAGE_KEY);
    return false;
  }

  return Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS;
};

const canReloadPage = (): boolean => {
  return typeof window !== 'undefined' && typeof window.location?.reload === 'function';
};

export const lazyWithRetry = async <T>(importer: () => Promise<T>): Promise<T> => {
  try {
    const module = await importer();
    safeSessionStorage.removeItem(RELOAD_STORAGE_KEY);
    return module;
  } catch (rawError) {
    const hasReloaded = memoryHasReloaded || safeSessionStorage.getItem(RELOAD_STORAGE_KEY) === 'true';

    if (!hasReloaded && !hasRecentReloadAttempt() && isChunkLoadError(rawError)) {
      const message = rawError instanceof Error ? rawError.message : String(rawError);

      if (!canReloadPage()) {
        logger.warn('Lazy chunk load failed but page reload is unavailable in this runtime.', {message});
        throw rawError;
      }

      logger.warn('Lazy chunk load failed, reloading page to fetch latest assets.', {message});
      // Set in-memory flags first so re-entry is blocked even if sessionStorage
      // is unavailable (Safari private browsing).
      memoryHasReloaded = true;
      memoryLastReloadAt = Date.now();
      safeSessionStorage.setItem(RELOAD_STORAGE_KEY, 'true');
      safeSessionStorage.setItem(LAST_RELOAD_AT_STORAGE_KEY, String(Date.now()));
      window.location.reload();

      return new Promise<T>(() => {
        // Intentionally unresolved: keep Suspense fallback until navigation completes.
      });
    }

    throw rawError;
  }
};
